import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BulkActionBar,
  ClassAssignmentPicker,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  LoadingButton,
  PagedDataTable,
  SearchField,
  StatusChip,
} from "src/grading/components";
import { PickedClass } from "src/grading/components/ClassAssignmentPicker/ClassAssignmentPicker";
import type { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import {
  REFUSAL_CODES,
  RefusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import {
  AssignmentRole,
  BulkResult,
  BulkStaffPayload,
  STAFF_ROLES,
  StaffRole,
  StaffStatus,
  StaffSummary,
  fullName,
} from "src/models/staff";
import {
  useBulkStaffMutation,
  useListStaffQuery,
} from "src/store/reducers/staff/staffApiSlice";
import InviteStaffDialog from "./InviteStaffDialog";
import PersonPanel from "./PersonPanel";
import usePeopleScope from "./usePeopleScope";
import { usePeopleUrlState } from "./usePeopleUrlState";

export interface StaffTabProps {
  /** A fixed role filter (the District admins tab); hides the role select. */
  roles?: StaffRole[];
  /** The role the invite dialog locks to, with `inviteLabel` as its title. */
  inviteRole?: StaffRole;
  inviteLabel?: string;
  tableLabel?: string;
  emptyTitle?: string;
  emptyBody?: string;
  /** The platform admin's district name, shown locked in the invite dialog. */
  districtName?: string;
}

const FILTER_ROLES: StaffRole[] = ["district_admin", "school_admin", "teacher"];
const STATUSES: StaffStatus[] = ["active", "invited", "deactivated"];
const NAMED_LIMIT = 5;

const isStaffRole = (value?: string): value is StaffRole =>
  (STAFF_ROLES as readonly string[]).includes(value ?? "");
const isStatus = (value?: string): value is StaffStatus =>
  (STATUSES as readonly string[]).includes(value ?? "");

/** Staff in the viewer's scope: search, filters, paging, bulk actions and the person panel. */
export const StaffTab = ({
  roles: fixedRoles,
  inviteRole,
  inviteLabel,
  tableLabel,
  emptyTitle,
  emptyBody,
  districtName,
}: StaffTabProps) => {
  const { t, i18n } = useTranslation();
  const assignTitleId = useId();
  const url = usePeopleUrlState();
  const scope = usePeopleScope(url.district);
  const tableRef = useRef<HTMLDivElement>(null);
  const known = useRef(new Map<string, StaffSummary>());

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [result, setResult] = useState<ReactNode>(undefined);
  /** The selection the open deactivate dialog names; kept while the dialog closes. */
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmText, setConfirmText] = useState({
    count: 0,
    names: "",
    typed: undefined as string | undefined,
  });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignClasses, setAssignClasses] = useState<PickedClass[]>([]);
  const [assignRole, setAssignRole] = useState<AssignmentRole>("teacher");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState<string | null>(null);

  const roleFilter = isStaffRole(url.role) ? url.role : undefined;
  const statusFilter = isStatus(url.status) ? url.status : undefined;
  const { data, isLoading, isFetching, isError, refetch } = useListStaffQuery({
    districtId: scope.requestDistrictId,
    schoolId: url.school,
    roles: fixedRoles ?? (roleFilter ? [roleFilter] : undefined),
    status: statusFilter,
    search: url.q || undefined,
    limit: rowsPerPage,
    offset: url.page * rowsPerPage,
  });
  const [bulkStaff, { isLoading: bulkRunning }] = useBulkStaffMutation();

  const rows = data?.items ?? [];
  rows.forEach((person) => known.current.set(person.id, person));

  const canUpdate = scope.has(permissions.UPDATE_STAFF);
  const canAssign = canUpdate && scope.has(permissions.UPDATE_CLASSES);
  const canInvite =
    scope.has(permissions.INVITE_STAFF) &&
    (inviteRole
      ? scope.grantableRoles.includes(inviteRole)
      : scope.grantableRoles.length > 0);
  const filtered = Boolean(
    url.q || url.school || (!fixedRoles && url.role) || url.status,
  );
  const roleName = (role: string) => t(`roles.${role}`);

  const selectedPeople = selectedIds.map((id) => known.current.get(id));
  const selectedNames = selectedPeople.map((person, index) =>
    person ? fullName(person) : selectedIds[index],
  );
  const namesText =
    selectedNames.length > NAMED_LIMIT
      ? t("people.andMore", {
          names: selectedNames.slice(0, NAMED_LIMIT).join(", "),
          count: selectedNames.length - NAMED_LIMIT,
        })
      : selectedNames.join(", ");
  const selectedAdmins = selectedPeople.filter((person) =>
    person?.roles.includes("district_admin"),
  );
  const typedConfirmation =
    selectedAdmins.length === 0
      ? undefined
      : selectedIds.length === 1
        ? selectedAdmins[0]?.email
        : t("staff.bulk.typeWord");

  const describe = (outcome: BulkResult) => {
    const refused = outcome.results.filter((item) => item.status !== "ok");
    const updated = outcome.results.length - refused.length;
    return (
      <>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {t("staff.bulk.result", { updated, skipped: refused.length })}
        </Box>
        {refused.length ? (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {refused.map((item) => {
              const person = known.current.get(item.id);
              const code = (REFUSAL_CODES as readonly string[]).includes(
                item.code ?? "",
              )
                ? (item.code as RefusalCode)
                : undefined;
              return (
                <li key={item.id}>
                  {t("staff.bulk.reason", {
                    name: person ? fullName(person) : item.id,
                    reason: code
                      ? refusalCopy(t, code)
                      : (item.message ?? t("staff.bulk.notChanged")),
                  })}
                </li>
              );
            })}
          </Box>
        ) : null}
      </>
    );
  };

  const runBulk = async (payload: Omit<BulkStaffPayload, "ids">) => {
    setResult(undefined);
    try {
      const outcome = await bulkStaff({
        ...payload,
        ids: selectedIds,
      }).unwrap();
      setSelectedIds([]);
      setResult(describe(outcome));
    } catch {
      setResult(t("staff.bulk.actionError"));
    }
  };

  const deactivate = async () => {
    await runBulk({ action: "deactivate" });
    setConfirmDeactivate(false);
  };

  const assign = async () => {
    await runBulk({
      action: "assign-classes",
      classIds: assignClasses.map((item) => item.classId),
      role: assignRole,
    });
    setAssignOpen(false);
    setAssignClasses([]);
  };

  const inviteButton = canInvite ? (
    <Button variant="contained" onClick={() => setInviteOpen(true)}>
      {inviteLabel ?? t("staff.invite")}
    </Button>
  ) : null;

  const columns: DataTableColumn<StaffSummary>[] = [
    {
      key: "name",
      header: t("staff.columns.name"),
      render: (person) => (
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => url.setFilters({ person: person.id })}
            sx={{
              p: 0,
              minWidth: 0,
              justifyContent: "flex-start",
              fontSize: 14,
              fontWeight: 800,
              color: "text.primary",
              textAlign: "left",
            }}
          >
            {fullName(person)}
          </Button>
          {fullName(person) !== person.email ? (
            <Box
              component="span"
              sx={{ fontSize: 13, color: "text.secondary" }}
            >
              {person.email}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: "role",
      header: t("staff.columns.role"),
      render: (person) =>
        person.roles
          .filter((role) => isStaffRole(role))
          .map(roleName)
          .join(", "),
    },
    {
      key: "school",
      header: t("staff.columns.school"),
      render: (person) => person.organization?.name ?? "—",
    },
    {
      key: "classes",
      header: t("staff.columns.classes"),
      align: "right",
      render: (person) => person.classCount,
    },
    {
      key: "status",
      header: t("staff.columns.status"),
      render: (person) => <StatusChip status={person.status} />,
    },
    {
      key: "lastActive",
      header: t("staff.columns.lastActive"),
      render: (person) =>
        person.lastActiveAt
          ? new Date(person.lastActiveAt).toLocaleDateString(i18n.language)
          : t("staff.lastActiveNever"),
    },
  ];

  const emptyState = filtered ? (
    <EmptyState
      headingLevel={3}
      title={t("people.noMatchTitle")}
      body={t("people.noMatchBody")}
      action={
        <Button variant="outlined" onClick={url.clearFilters}>
          {t("people.clearFilters")}
        </Button>
      }
    />
  ) : (
    <EmptyState
      headingLevel={3}
      title={emptyTitle ?? t("staff.emptyTitle")}
      body={emptyBody ?? t("staff.emptyBody")}
      action={inviteButton}
    />
  );

  return (
    <Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <SearchField
          label={t("staff.searchLabel")}
          value={url.q}
          onChange={(q) => url.setFilters({ q })}
          sx={{ width: { xs: "100%", sm: 280 } }}
        />
        {scope.schools.length > 0 ? (
          <TextField
            select
            size="small"
            label={t("staff.filters.school")}
            value={url.school ?? ""}
            onChange={(event) =>
              url.setFilters({ school: event.target.value || null })
            }
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t("staff.filters.allSchools")}</MenuItem>
            {scope.schools.map((school) => (
              <MenuItem key={school.id} value={school.id}>
                {school.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {fixedRoles ? null : (
          <TextField
            select
            size="small"
            label={t("staff.filters.role")}
            value={roleFilter ?? ""}
            onChange={(event) =>
              url.setFilters({ role: event.target.value || null })
            }
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t("staff.filters.allRoles")}</MenuItem>
            {FILTER_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {roleName(role)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label={t("staff.filters.status")}
          value={statusFilter ?? ""}
          onChange={(event) =>
            url.setFilters({ status: event.target.value || null })
          }
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t("staff.filters.allStatuses")}</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {t(`console.status.${status}`)}
            </MenuItem>
          ))}
        </TextField>
        {inviteButton ? (
          <Box sx={{ ml: { sm: "auto" } }}>{inviteButton}</Box>
        ) : null}
      </Stack>

      {invited ? (
        <Box sx={{ mb: 1.5 }}>
          <InlineAlert severity="success">
            {t("staff.invited", { email: invited })}
          </InlineAlert>
        </Box>
      ) : null}

      <BulkActionBar
        count={selectedIds.length}
        onClear={() => setSelectedIds([])}
        result={result}
        returnFocusTo={() =>
          tableRef.current?.querySelector<HTMLElement>('input[type="checkbox"]')
        }
        actions={
          canUpdate ? (
            <>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={bulkRunning}
                onClick={() => {
                  setConfirmText({
                    count: selectedIds.length,
                    names: namesText,
                    typed: typedConfirmation,
                  });
                  setConfirmDeactivate(true);
                }}
              >
                {t("staff.bulk.deactivate")}
              </Button>
              <LoadingButton
                size="small"
                variant="outlined"
                color="inherit"
                loading={bulkRunning && !confirmDeactivate && !assignOpen}
                disabled={bulkRunning}
                onClick={() => runBulk({ action: "reactivate" })}
                label={t("staff.bulk.reactivate")}
              />
              {canAssign ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  disabled={bulkRunning}
                  onClick={() => setAssignOpen(true)}
                >
                  {t("staff.bulk.assign")}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {isError ? (
        <InlineAlert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {t("staff.loadError")}
        </InlineAlert>
      ) : (
        <Box ref={tableRef}>
          <PagedDataTable<StaffSummary>
            aria-label={tableLabel ?? t("staff.tableLabel")}
            columns={columns}
            rows={rows}
            getRowKey={(person) => person.id}
            getRowLabel={fullName}
            loading={isLoading || (isFetching && rows.length === 0)}
            total={data?.total ?? 0}
            page={url.page}
            rowsPerPage={rowsPerPage}
            onPageChange={(page) => url.setFilters({ page })}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              url.setFilters({ page: 0 });
            }}
            selectable={canUpdate}
            selectedIds={selectedIds}
            onSelectionChange={(ids) => setSelectedIds(ids.map(String))}
            emptyState={emptyState}
          />
        </Box>
      )}

      <ConfirmDialog
        open={confirmDeactivate}
        tone="destructive"
        title={t("staff.bulk.deactivateTitle", { count: confirmText.count })}
        body={t("staff.bulk.deactivateBody", { names: confirmText.names })}
        confirmLabel={t("staff.bulk.deactivate")}
        typedConfirmation={confirmText.typed}
        loading={bulkRunning}
        onConfirm={deactivate}
        onClose={() => setConfirmDeactivate(false)}
      />

      <Dialog
        open={assignOpen}
        onClose={bulkRunning ? undefined : () => setAssignOpen(false)}
        aria-labelledby={assignTitleId}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id={assignTitleId} component="h2">
          {t("staff.assign.title")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <DialogContentText>
            {t("staff.assign.body")} {namesText}
          </DialogContentText>
          <ClassAssignmentPicker
            mode="byPerson"
            label={t("staff.assign.classes")}
            districtId={scope.requestDistrictId}
            schoolId={url.school}
            showRoles={false}
            value={assignClasses}
            onChange={setAssignClasses}
          />
          <TextField
            select
            size="small"
            label={t("staff.assign.role")}
            value={assignRole}
            onChange={(event) =>
              setAssignRole(event.target.value as AssignmentRole)
            }
          >
            {(["teacher", "aide"] as const).map((role) => (
              <MenuItem key={role} value={role}>
                {t(`staff.assignmentRole.${role}`)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            color="inherit"
            disabled={bulkRunning}
            onClick={() => setAssignOpen(false)}
          >
            {t("common.cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            loading={bulkRunning}
            disabled={assignClasses.length === 0}
            onClick={assign}
            label={t("staff.assign.submit")}
          />
        </DialogActions>
      </Dialog>

      <InviteStaffDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={setInvited}
        grantableRoles={scope.grantableRoles}
        districtId={scope.requestDistrictId}
        districtName={districtName}
        role={inviteRole}
        title={inviteLabel}
      />

      {url.person ? (
        <PersonPanel
          profileId={url.person}
          districtId={scope.requestDistrictId}
          onClose={() => url.setFilters({ person: null })}
        />
      ) : null}
    </Box>
  );
};

export default StaffTab;
